<template>
  <toggle-box v-if="isDesktop" id="filter-box" :text="$t('FILTERS')" rounded-corner ref="box" @show="checkShow" @close="clickOutside">
    <template v-slot:content>
      <div id='menu'>
        <table>
          <tr>
            <td class='label'>{{ $t('APPROVED_AMOUNT') }}</td>
            <td>
              <div class='container'>
                <input v-model="minApprovedAmount" @keypress="onlyMoney"/>
                <div class='sub'>-</div>
                <input v-model="maxApprovedAmount" @keypress="onlyMoney"/>
              </div>
              <small class="message" v-show="!valid">{{ message }}</small>
            </td>
          </tr>

          <tr>
            <td class='label'>{{ $t('BRANCH') }}</td>
            <td>
              <div class='container'>
                <filter-button class="btn" :key="key" v-for="(value, key) in branches" :text="key" :active="value" @click="clickBranch(key)" />
              </div>
            </td>
          </tr>

          <tr>
            <td class='label'>{{ $t('ASSET_TYPE') }}</td>
            <td>
              <div class='container'>
                <filter-button class="btn" :key="key" v-for="(value, key) in assetTypes" :text="key" :active="value" @click="clickAssetType(key)" />
              </div>
            </td>
          </tr>
        </table>

        <div class="right">
          <button
            class="clear-btn button"
            v-on:click="clear"
            type="button"
          >
            <span>{{ $t('CLEAR') }}</span>
          </button>
          <button
            class="filter-btn button"
            :class="{
              disabled: !valid,
            }"
            v-on:click="filter"
            type="button"
          >
            <span>{{ $t('FILTER') }}</span>
          </button>
        </div>
      </div>
    </template>
  </toggle-box>
  <div id="filter-box" v-else>
    <icon-button geranium :size="35" :icon="faFilter" @click="() => filterBoxBottomSheetOpen = !filterBoxBottomSheetOpen"/>
    <bottom-sheet v-model="filterBoxBottomSheetOpen" :openY="0.31">
      <div class="padding-bottom">
        <div class='label'>{{ $t('APPROVED_AMOUNT') }}:</div>
        <div class="padding-bottom">
          <div class='container'>
            <input v-model="minApprovedAmount" @keypress="onlyMoney"/>
            <div class='sub'>-</div>
            <input v-model="maxApprovedAmount" @keypress="onlyMoney"/>
          </div>
          <small class="message" v-show="!valid">{{ message }}</small>
        </div>
        <div class='label padding-bottom'>{{ $t('BRANCH') }}:</div>
        <div class='padding-bottom container'>
          <filter-button class="btn" :key="key" v-for="(value, key) in branches" :text="key" :active="value" @click="clickBranch(key)" />
        </div>
        <div class='label padding-bottom'>{{ $t('ASSET_TYPE') }}:</div>
        <div class='padding-bottom container'>
          <filter-button class="btn" :key="key" v-for="(value, key) in assetTypes" :text="key" :active="value" @click="clickAssetType(key)" />
        </div>
      </div>
      <div class="d-flex justify-content-around">
        <button
          class="clear-btn button"
          v-on:click="clear"
          type="button"
        >
          <span>{{ $t('CLEAR') }}</span>
        </button>
        <button
          class="filter-btn button"
          :class="{
            disabled: !valid,
          }"
          v-on:click="filter"
          type="button"
        >
          <span>{{ $t('FILTER') }}</span>
        </button>
      </div>
    </bottom-sheet>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from '@vue/composition-api';
import { isNumber } from '@/helpers';
import ToggleBox from '@/components/ToggleBox';
import IconButton from '@/components/IconButton';
import BottomSheet from '@/components/BottomSheet';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import FilterButton from './FilterButton.vue';

const { useState: useAppState, useActions: useAppActions } = createNamespacedHelpers('app');

export default {
  name: 'FilterBox',
  components: {
    ToggleBox,
    IconButton,
    BottomSheet,
    FilterButton,
  },
  props: {
    defaultBranches: {
      type: Array,
      default: () => []
    },
    defaultAssetTypes: {
      type: Array,
      default: () => []
    },
    defaultMinApprovedAmount: String,
    defaultMaxApprovedAmount: String,
  },
  setup(props, { refs, root, emit }) {
    const branches = ref({});
    const assetTypes = ref({});
    const minApprovedAmount = ref('');
    const maxApprovedAmount = ref('');
    const oldBranches = ref({});
    const oldAssetTypes = ref({});
    const oldMinValue = ref('');
    const oldMaxValue = ref('');
    const valid = ref(true);
    const message = ref('');
    const filterBoxBottomSheetOpen = ref(false);

    const { branches: branchesState, assetTypes: assetTypesState, isDesktop } = useAppState(['branches', 'assetTypes', 'isDesktop']);
    const getMisc = useAppActions(['getMisc']);

    const branchKey = computed(() => branchesState.value.filter((branch) => branch.name !== 'HO').reduce((result, branch) => {
      const newResult = { ...result };
      newResult[branch.name] = branch.id;
      return newResult;
    }, {}));

    const assetTypeKey = computed(() => assetTypesState.value.reduce((result, assetType) => {
      const newResult = { ...result };
      newResult[assetType.name] = assetType.id;
      return newResult;
    }, {}));

    const checkShow = () => {
      oldBranches.value = { ...branches.value };
      oldAssetTypes.value = { ...assetTypes.value };
      oldMinValue.value = minApprovedAmount.value;
      oldMaxValue.value = maxApprovedAmount.value;
    };

    const clickOutside = () => {
      branches.value = { ...oldBranches.value };
      assetTypes.value = { ...oldAssetTypes.value };
      minApprovedAmount.value = oldMinValue.value;
      maxApprovedAmount.value = oldMaxValue.value;
    };

    const clickBranch = (name) => { branches.value[name] = !branches.value[name]; };
    const clickAssetType = (name) => { assetTypes.value[name] = !assetTypes.value[name]; };

    const onlyMoney = (e) => {
      const charCode = (e.which) ? e.which : e.keyCode;

      if (e.target.value.includes('.') && charCode === 46) {
        e.preventDefault();
        return false;
      }

      const decimalValue = e.target.value.split('.')[1];
      if (decimalValue !== undefined && decimalValue.length === 3) {
        e.preventDefault();
        return false;
      }

      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        e.preventDefault();
        return false;
      }

      return true;
    };

    const checkValid = () => {
      let value = true;
      if (!isNumber(minApprovedAmount.value) && minApprovedAmount.value.trim() !== '') {
        value = false;
      }
      if (!isNumber(maxApprovedAmount.value) && maxApprovedAmount.value.trim() !== '') {
        value = false;
      }
      if (isNumber(minApprovedAmount.value) && isNumber(maxApprovedAmount.value)) {
        if (Number.parseFloat(maxApprovedAmount.value) < Number.parseFloat(minApprovedAmount.value)) {
          value = false;
        }
      }
      valid.value = value;
      if (!value) {
        message.value = root.$t('INVALID_ERROR');
      }
    };

    const setFilterButton = () => {
      const defaultBranchesObject = props.defaultBranches.reduce((result, branch) => {
        const newResult = { ...result };
        newResult[branch] = true;
        return newResult;
      }, {});

      branches.value = branchesState.value.filter((branch) => branch.name !== 'HO').reduce((result, branch) => {
        const newResult = { ...result };
        newResult[branch.name] = !!defaultBranchesObject[branch.id];
        return newResult;
      }, {});

      const defaultAssetTypesObject = props.defaultAssetTypes.reduce((result, assetType) => {
        const newResult = { ...result };
        newResult[assetType] = true;
        return newResult;
      }, {});

      assetTypes.value = assetTypesState.value.reduce((result, assetType) => {
        const newResult = { ...result };
        newResult[assetType.name] = !!defaultAssetTypesObject[assetType.id];
        return newResult;
      }, {});

      minApprovedAmount.value = props.defaultMinApprovedAmount === null ? '' : props.defaultMinApprovedAmount;
      maxApprovedAmount.value = props.defaultMaxApprovedAmount === null ? '' : props.defaultMaxApprovedAmount;
      checkValid();
    };

    const clear = () => {
      minApprovedAmount.value = '';
      maxApprovedAmount.value = '';
      Object.keys(branches.value).forEach((name) => { branches.value[name] = false; });
      Object.keys(assetTypes.value).forEach((name) => { assetTypes.value[name] = false; });
      oldMinValue.value = minApprovedAmount.value;
      oldMaxValue.value = maxApprovedAmount.value;
      oldBranches.value = { ...branches.value };
      oldAssetTypes.value = { ...assetTypes.value };
    };

    const filter = () => {
      const value = {
        minApprovedAmount: minApprovedAmount.value.trim() === '' ? null : minApprovedAmount.value,
        maxApprovedAmount: maxApprovedAmount.value.trim() === '' ? null : maxApprovedAmount.value
      };

      value.branches = [];

      Object.keys(branches.value).forEach((name) => {
        if (branches.value[name]) {
          value.branches.push(branchKey.value[name]);
        }
      });

      value.assetTypes = [];

      Object.keys(assetTypes.value).forEach((name) => {
        if (assetTypes.value[name]) {
          value.assetTypes.push(assetTypeKey.value[name]);
        }
      });

      emit('filter', value);
      if (isDesktop.value) {
        refs.box.toggle(false);
      } else {
        filterBoxBottomSheetOpen.value = false;
      }
    };

    onMounted(async () => {
      if (branchesState.value.length === 0 || assetTypesState.value.length === 0) {
        await getMisc();
      }
      setFilterButton();
    });

    watch(minApprovedAmount, checkValid);
    watch(maxApprovedAmount, checkValid);

    return {
      branches,
      assetTypes,
      isDesktop,
      faFilter,
      minApprovedAmount,
      maxApprovedAmount,
      oldBranches,
      oldAssetTypes,
      oldMinValue,
      oldMaxValue,
      valid,
      message,
      filterBoxBottomSheetOpen,
      checkShow,
      clickOutside,
      clickBranch,
      clickAssetType,
      onlyMoney,
      checkValid,
      setFilterButton,
      clear,
      filter,
    };
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/colors";

#filter-box {

  @media (max-width: 991px) {
    /deep/ .bottom-sheet {


      .card {
        padding: 20px 20px 0px;
      }

      .padding-bottom {
        padding-bottom: 10px;
      }
    }
  }

  input {
    border: 1px solid $dove;
    border-radius: 0;
    font-family: "Source Sans Pro", Arial, Tahoma;
    font-size: 15px;
    background-color: $babyPowder;
    color: $independence;
    height: 28px;
    width: 147px;
    @media (max-width: 991px) {
      width: 156px;
    }
    outline: 0;
    border-width: 0 0 1px;
    padding: 0;

    &::placeholder {
      color: $alto;
    }

    &:active, &:focus {
      outline: none;
    }
  }

  .label {
    text-align: left;
    color: $trout;
    font-family: "Source Sans Pro", Arial, Tahoma;
    font-size: 18px;
    text-transform: capitalize;
    width: 20%;
    @media (max-width: 991px) {
      width: 100%;
    }
  }

  .sub {
    padding: 0 5px;
  }

  .btn {
    margin: 2px;
    @media (max-width: 991px) {
      margin: 4px 6px;
    }
  }

  .container {
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;

    @media (max-width: 991px) {
      padding: 0px;
    }
  }

  .filter-btn {
    height: 30px;
    border: 0px;
    border-radius: 20px;
    display: inline-block;
    box-sizing: border-box;
    padding: 0 20px;
    color: $babyPowder;
    background-color: $geranium;
    font-size: 16px;
    font-weight: bold;
    transition: all .2s ease-in-out;
    cursor: pointer;
    position: relative;
    user-select: none;
    box-shadow: 0px 2px 4px #00000029;
    text-transform: capitalize;

    .icon {
      margin-left: 10px;
    }

    &:hover {
      background-color: $carmine;
    }

    &:active, &:focus {
      outline: none;
    }

    &.disabled {
      background-color: $grey;
      border-color: $grey;
      pointer-events: none;
    }
  }

  .clear-btn {
    height: 30px;
    border: 0px;
    border-radius: 20px;
    display: inline-block;
    box-sizing: border-box;
    padding: 0 20px;
    color: $white;
    background-color: $independence;
    font-size: 16px;
    font-weight: bold;
    transition: all .2s ease-in-out;
    cursor: pointer;
    position: relative;
    user-select: none;
    box-shadow: 0px 2px 4px #00000029;
    text-transform: capitalize;

    .icon {
      margin-left: 10px;
    }

    &:hover {
      background-color: $carmine;
    }

    &:active, &:focus {
      outline: none;
    }

    &.disabled {
      background-color: $grey;
      border-color: $grey;
      pointer-events: none;
    }
  }

  #menu {
    background: $white;
    width: 460px;

    @media (max-width: 991px) {
      width: 330px;
    }

    table {
      margin: 20px 10px;

      tr {
        td {
          vertical-align:top;
        }
      }

      tr:not(:first-child) {
        td {
          padding-top: 20px;
        }
      }
    }

    .right {
      display: flex;
      justify-content: flex-end;
      margin: 0px 30px;

      @media (max-width: 991px) {
        margin: 0px 10px;
      }

      .button {
        margin-left: 5px;
      }
    }

    small {
      &.message {
        color: $geranium;
        padding-left: 15px;
      }
    }
  }
}
</style>
